import React, { useEffect, useState, useCallback } from 'react';
import { Select, Card, Tag, Space, Empty, message, Spin, Button, Statistic, Row, Col, Tooltip } from 'antd';
import { Line } from '@ant-design/plots';
import http from '../api/http';

// 员工安全画像页面：
// 1. 选择员工（下拉搜索） -> 拉取 /api/behavior/profiles/:employee_id
// 2. 展示当前风险分、Top标签（标签名称 + 风险等级色）、趋势折线（trend_series: [{date, score}]）
// 3. 若无画像数据，提示暂无并允许选择其他员工
// 4. 可扩展：标签权重、行为事件最近列表、对比同部门平均值等

const riskColors = { '低': 'green', '中': 'blue', '高': 'orange', '极高': 'red' };

const SafetyProfilePage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState();
  const [profile, setProfile] = useState(null);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [tagsMap, setTagsMap] = useState({});
  const [trendData, setTrendData] = useState([]); // 合并后的趋势显示数据
  const [loadingTrend, setLoadingTrend] = useState(false);

  const loadEmployees = useCallback(async () => {
    setLoadingEmp(true);
    try {
      const { data } = await http.get('/employees', { params: { status: '在职' } });
      if (Array.isArray(data)) setEmployees(data); else message.error('员工加载失败');
    } catch(e){ message.error(e.message || '员工加载失败'); }
    finally { setLoadingEmp(false); }
  }, []);

  const loadTags = useCallback( async () => {
    try {
      const { data } = await http.get('/behavior/tags', { params: { status: 'active' } });
      if (Array.isArray(data)) {
        const map = {}; data.forEach(t => map[t.id] = t);
        setTagsMap(map);
      }
    } catch(e){ /* ignore */ }
  }, []);

  const loadProfile = useCallback( async (id) => {
    if(!id) return;
    setLoadingProfile(true);
    try {
      const { data } = await http.get('/behavior/profiles/' + id);
      setProfile(data);
    } catch(e){ message.error(e.message || '画像加载失败'); }
    finally { setLoadingProfile(false); }
  }, []);

  const loadTrend = useCallback(async (id) => {
    if(!id) return;
    setLoadingTrend(true);
    try {
      const { data } = await http.get('/behavior/profiles/' + id + '/trend');
      const combined = [];
      if(Array.isArray(data.trend)){
        data.trend.forEach(p=> combined.push({ date: p.date, score: p.score, type: '个人' }));
      }
      if(Array.isArray(data.dept_avg_trend)){
        data.dept_avg_trend.forEach(p=> combined.push({ date: p.date, score: p.score, type: '部门平均' }));
      }
      setTrendData(combined);
    } catch(e){ message.error(e.message || '趋势加载失败'); }
    finally { setLoadingTrend(false); }
  }, []);

  useEffect(() => {
    loadEmployees();
    loadTags();
  }, [loadEmployees, loadTags]);

  useEffect(() => { if(selectedId){ loadProfile(selectedId); loadTrend(selectedId);} }, [selectedId, loadProfile, loadTrend]);

  // 趋势图配置
  const trendConfig = {
    data: trendData,
    xField: 'date',
    yField: 'score',
    seriesField: 'type',
    smooth: true,
    height: 260,
    point: { size: 4 },
    meta: { score: { alias: '风险分' } },
    tooltip: { showMarkers: true }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 12 }}>员工安全画像</h2>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          showSearch
          allowClear
          placeholder="选择员工"
          style={{ width: 240 }}
          value={selectedId}
          onChange={v => setSelectedId(v)}
          loading={loadingEmp}
          options={employees.map(e => ({ label: e.name + (e.department? ' / ' + e.department : ''), value: e.id }))}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
  {selectedId && <Button onClick={() => { loadProfile(selectedId); loadTrend(selectedId); }}>刷新画像</Button>}
      </Space>
      {!selectedId && <Empty description="请选择员工查看画像" />}
      {selectedId && (
        <Spin spinning={loadingProfile} tip="加载画像...">
          {profile && (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Card title="当前风险概览" bordered>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic title="风险分" value={profile.current_risk_score !== null ? profile.current_risk_score : '—'} valueStyle={{ fontSize: 28 }} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="部门平均" value={profile.dept_avg_score !== null ? profile.dept_avg_score.toFixed(1) : '—'} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="部门排名" value={profile.dept_rank !== null ? `${profile.dept_rank} / ${profile.dept_member_count}` : '—'} />
                  </Col>
                </Row>
                {profile.current_risk_score !== null && profile.dept_avg_score !== null && (
                  <div style={{ marginTop: 12, fontSize: 13, color: '#555' }}>
                    与部门平均差值：<Tag color={profile.current_risk_score - profile.dept_avg_score >= 0 ? 'red':'green'}>
                      { (profile.current_risk_score - profile.dept_avg_score >=0 ? '+' : '') + (profile.current_risk_score - profile.dept_avg_score).toFixed(1) }
                    </Tag>
                    {profile.dept_rank !== null && (
                      <Tooltip title="排名基于风险分由高到低">当前排名越靠前表示风险相对更高</Tooltip>
                    )}
                  </div>
                )}
                <div style={{ color: '#888', marginTop: 4 }}>最近更新时间：{profile.updated_at || '—'}{profile.department? ` / 部门：${profile.department}`:''}</div>
              </Card>
              <Card title="Top标签" bordered>
                {profile.top_tags && profile.top_tags.length ? (
                  <Space wrap>
                    {profile.top_tags.map(t => {
                      const fullTag = tagsMap[t.id] || {}; // t 可能是 {id, name, risk_level}
                      const name = t.name || fullTag.name || ('标签#'+t.id);
                      const risk = t.risk_level || fullTag.risk_level || '中';
                      return <Tag key={t.id} color={riskColors[risk] || 'default'}>{name} / {risk}</Tag>;
                    })}
                  </Space>
                ) : <Empty description="暂无标签" />}
              </Card>
              <Card title="风险趋势" bordered extra={<span style={{fontSize:12,color:'#666'}}>{loadingTrend? '加载中...' : '个人vs部门'}</span>}>
                {trendData && trendData.length ? (
                  <Spin spinning={loadingTrend}>
                    <Line {...trendConfig} />
                  </Spin>
                ) : <Empty description={loadingTrend? '加载中...' : '暂无趋势数据'} />}
              </Card>
            </Space>
          )}
          {!profile && !loadingProfile && <Empty description="暂无画像数据" />}
        </Spin>
      )}
    </div>
  );
};

export default SafetyProfilePage;
